/**
 * Adding dayjs plugins for handling timezones and UTC
 */
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Returns a Moment object representing the date from the specified number of days ago
 * @param dayCount
 */
export const daysAgo = (dayCount: number) => {
  return dayjs().subtract(dayCount, 'days');
};

/**
 * Returns true if the specified date is after the specified number of days ago
 * @param date - Date to test, Moment object, or ISO string
 * @param after - Number of days ago or Moment object representing the date to test.
 */
export const dateIsAfter = (date: Date | Dayjs | string, after: number | Dayjs) => {
  // Ensure both `date` and `after` are Moment objects
  const dateMoment = dayjs(date);
  const afterMoment = dayjs.isDayjs(after) ? after : daysAgo(after);

  return dateMoment.isAfter(afterMoment);
};

/**
 * Formats a timestamp to a readable UTC string
 * Handles both ISO strings and Unix timestamps
 * @param timestamp - ISO string, Unix timestamp string, or Unix timestamp number
 * @returns Formatted timestamp string with UTC timezone
 */
export const formatTimestampToUTC = (timestamp: string | number): string => {
  try {
    let momentDate: Dayjs;

    if (typeof timestamp === 'string') {
      // Try to parse string with Moment first
      momentDate = dayjs(timestamp);

      // If invalid, try as Unix timestamp (seconds)
      if (!momentDate.isValid()) {
        momentDate = dayjs.unix(parseInt(timestamp, 10));
      }
    } else {
      // Handle numeric Unix timestamp
      momentDate = dayjs.unix(timestamp);
    }

    if (!momentDate.isValid()) {
      return 'Unknown Time';
    }

    return `${momentDate.utc().format('M/D/YYYY, h:mm:ss A')} UTC`;
  } catch {
    return 'Unknown Time';
  }
};

/**
 * Takes an epoch timestamp and formats it to local time
 * @param epoch
 * @param format
 */
export const formatEpochToLocal = (epoch: string | number, format = 'M/D/YYYY, h:mm:ss A') => {
  return dayjs
    .unix(typeof epoch === 'string' ? parseInt(epoch, 10) : epoch)
    .local()
    .format(format);
};

export const formatTimestampToLocal = (timestamp: string | number, format: string) => {
  const utcString = formatTimestampToUTC(timestamp);
  if (utcString === 'Unknown Time') {
    return utcString;
  }
  return dayjs.utc(utcString, 'M/D/YYYY, h:mm:ss A UTC').local().format(format);
};

/**
 * Returns the default display month for the Scenario Management header controls.
 * Returns the previous month if the current day is less than 23.
 * Returns the current month if the current day is greater than or equal to 23.
 */
export const getDefaultDisplayMonth = () => {
  const currMoment = dayjs();
  const currDay = Number(currMoment.format('D'));
  // Return previous month if Day is less than 23
  if (currDay < 23) return currMoment.subtract(1, 'months').format('M');
  // Return current month if Day is greater than or equal to 23
  return currMoment.format('M');
};
